// entities/combo/lib/comboUtils.ts — Pure domain utilities (no HTTP, no Vue)

import type { Combo, ComboGroup, ComboGroupProduct, ComboSelection } from '../model/types'

export function validateComboSelections(
  combo: Combo,
  selections: ComboSelection[],
): { valid: boolean; errors: string[] } {
  const errors: string[] = []

  for (const group of combo.groups) {
    const selection = selections.find(
      (s) => s.group_id.toString() === group.id.toString(),
    )

    if (group.is_required && !selection) {
      errors.push(`Группа "${group.name}" обязательна для выбора`)
      continue
    }

    if (!selection) continue

    const count = selection.product_ids.length

    if (count < group.min_selections) {
      errors.push(
        `В группе "${group.name}" нужно выбрать минимум ${group.min_selections} товар(ов)`,
      )
    }

    if (count > group.max_selections) {
      errors.push(
        `В группе "${group.name}" можно выбрать максимум ${group.max_selections} товар(ов)`,
      )
    }

    const validProductIds = new Set(group.products.map((p) => p.id.toString()))
    for (const productId of selection.product_ids) {
      if (!validProductIds.has(productId.toString())) {
        errors.push(`Товар с ID ${productId} не найден в группе "${group.name}"`)
      }
    }
  }

  return { valid: errors.length === 0, errors }
}

export function calculateComboPrice(
  combo: Combo,
  selections: ComboSelection[],
): { basePrice: number; discount: number; finalPrice: number } {
  let basePrice = 0
  let discountablePrice = 0

  for (const selection of selections) {
    const group = combo.groups.find(
      (g) => g.id.toString() === selection.group_id.toString(),
    )
    if (!group) continue

    for (const productId of selection.product_ids) {
      const product = group.products.find(
        (p) => p.id.toString() === productId.toString(),
      )
      if (product) {
        basePrice += product.price
        if (group.apply_discount) {
          discountablePrice += product.price
        }
      }
    }
  }

  const discount = discountablePrice * (combo.discount_percent / 100)
  const finalPrice = basePrice - discount

  return {
    basePrice,
    discount,
    finalPrice: Math.round(finalPrice * 100) / 100,
  }
}

export function getDefaultComboSelections(combo: Combo): ComboSelection[] {
  const selections: ComboSelection[] = []

  for (const group of combo.groups) {
    const defaultProducts = group.products
      .filter((p) => p.is_default)
      .map((p) => p.id)

    if (defaultProducts.length > 0) {
      selections.push({
        group_id: group.id,
        product_ids: defaultProducts.slice(0, group.max_selections),
      })
    } else if (group.is_required && group.products.length > 0) {
      selections.push({
        group_id: group.id,
        product_ids: group.products
          .slice(0, group.min_selections)
          .map((p) => p.id),
      })
    }
  }

  return selections
}

export function isComboAvailable(combo: Combo): boolean {
  return combo.is_active && combo.stock > 0
}

export function getSelectedComboProducts(
  combo: Combo,
  selections: ComboSelection[],
): Array<{ group: ComboGroup; product: ComboGroupProduct }> {
  const result: Array<{ group: ComboGroup; product: ComboGroupProduct }> = []

  for (const selection of selections) {
    const group = combo.groups.find(
      (g) => g.id.toString() === selection.group_id.toString(),
    )
    if (!group) continue

    for (const productId of selection.product_ids) {
      const product = group.products.find(
        (p) => p.id.toString() === productId.toString(),
      )
      if (product) result.push({ group, product })
    }
  }

  return result
}
